import { useState, useRef, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';
import type { CrossingSourceSelectorProps, CrossingSourceDocument } from './MasterDocument.types';
import { useMasterDocumentTranslation } from './i18n';
import { inlineActionButton, inlineActionButtonNeutral } from './inlineActions';

// ─── Async search input ────────────────────────────────────

function AsyncSearchInput({
  onAdd,
  onCancel,
  searchDocuments,
  alreadySelectedIds = [],
}: {
  onAdd: (doc: CrossingSourceDocument) => void;
  onCancel: () => void;
  searchDocuments: (query: string) => Promise<CrossingSourceDocument[]>;
  alreadySelectedIds?: string[];
}) {
  const { t } = useMasterDocumentTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CrossingSourceDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const search = useCallback(
    (value: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!value.trim()) {
        setResults([]);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const data = await searchDocuments(value.trim());
          setResults(data);
        } finally {
          setLoading(false);
        }
      }, 300);
    },
    [searchDocuments],
  );

  const handleSelect = (doc: CrossingSourceDocument) => {
    onAdd(doc);
    setQuery('');
    setResults([]);
    inputRef.current?.focus();
  };

  const visibleResults = results.filter((r) => !alreadySelectedIds.includes(r.id));

  return (
    <div className="relative mt-2">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            search(e.target.value);
          }}
          onKeyDown={(e) => e.key === 'Escape' && onCancel()}
          placeholder={t('master_document.crossing.searchPlaceholder')}
          className={clsx(
            'w-full px-2 py-1 pr-7 rounded-md text-sm',
            'border border-border-primary dark:border-dark-border-primary',
            'bg-bg-primary dark:bg-dark-bg-primary',
            'text-content-primary dark:text-dark-content-primary',
            'placeholder:text-content-secondary dark:placeholder:text-dark-content-secondary',
            'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 dark:focus:ring-dark-border-custom',
          )}
        />
        {loading && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-content-secondary dark:text-dark-content-secondary"
          >
            <path
              fillRule="evenodd"
              d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>

      {visibleResults.length > 0 && (
        <ul className={clsx(
          'absolute left-0 right-0 top-full mt-1 z-50 py-1 max-h-48 overflow-auto',
          'bg-bg-primary dark:bg-dark-bg-primary',
          'rounded-xl border border-border-primary dark:border-dark-border-primary',
          'shadow-[0_4px_16px_rgba(0,0,0,.12)] dark:shadow-[0_4px_16px_rgba(0,0,0,.4)]',
        )}>
          {visibleResults.map((doc) => (
            <li key={doc.id}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(doc);
                }}
                className={clsx(
                  'w-full text-left px-3 py-2 transition-colors',
                  'hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
                )}
              >
                <span className="block text-sm text-content-primary dark:text-dark-content-primary font-medium">
                  {doc.number}
                </span>
                <span className="block text-xs text-content-secondary dark:text-dark-content-secondary">
                  {doc.classLabel}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={onCancel}
        className={clsx(inlineActionButtonNeutral, 'mt-1')}
      >
        {t('master_document.chrome.cancel')}
      </button>
    </div>
  );
}

// ─── CrossingSourceSelector ────────────────────────────────

/**
 * CrossingSourceSelector — muestra y gestiona los documentos fuente (padre)
 * asociados a un documento de cruce.
 *
 * En modo `locked` solo presenta los chips de solo lectura para contexto visual.
 * Cuando se provee `searchDocuments`, usa búsqueda async con debounce.
 */
export function CrossingSourceSelector({
  selectedDocuments,
  onAdd,
  onRemove,
  locked = false,
  searchDocuments,
  className,
}: CrossingSourceSelectorProps) {
  const { t } = useMasterDocumentTranslation();
  const [showInput, setShowInput] = useState(false);

  return (
    <div
      className={clsx(
        'bg-bg-primary dark:bg-dark-bg-primary',
        'border border-border-primary dark:border-dark-border-primary',
        'rounded-xl p-3',
        className,
      )}
    >
      <div className="flex items-center gap-1.5 mb-2">
        {locked && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
            className="w-3 h-3 text-content-secondary dark:text-dark-content-secondary flex-shrink-0"
          >
            <path
              fillRule="evenodd"
              d="M8 1a3.5 3.5 0 0 0-3.5 3.5V7A1.5 1.5 0 0 0 3 8.5v4A1.5 1.5 0 0 0 4.5 14h7a1.5 1.5 0 0 0 1.5-1.5v-4A1.5 1.5 0 0 0 11 7V4.5A3.5 3.5 0 0 0 8 1Zm2 6V4.5a2 2 0 1 0-4 0V7h4Z"
              clipRule="evenodd"
            />
          </svg>
        )}
        <span className="text-xs font-bold text-content-secondary dark:text-dark-content-secondary uppercase tracking-wide">
          {t('master_document.crossing.sourceDocuments')}
        </span>
      </div>

      {selectedDocuments.length === 0 && !locked ? (
        <p className="text-sm text-content-secondary dark:text-dark-content-secondary mb-2">
          {t('master_document.crossing.selectSourceHint')}
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedDocuments.map((doc) => (
            <span
              key={doc.id}
              className={clsx(
                'inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium',
                'bg-primary-custom-100 text-primary-custom-700',
                'dark:bg-dark-bg-custom dark:text-dark-content-custom',
              )}
            >
              {doc.classLabel} · {doc.number}
              {!locked && (
                <button
                  type="button"
                  onClick={() => onRemove(doc.id)}
                  aria-label={`Quitar ${doc.classLabel} ${doc.number}`}
                  className={clsx(
                    'w-3 h-3 rounded-full flex items-center justify-center flex-shrink-0 transition-colors',
                    'hover:bg-primary-custom-300 dark:hover:bg-dark-border-custom',
                  )}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true" className="w-2 h-2">
                    <path d="M6.707 6l2.647-2.646a.5.5 0 1 0-.708-.708L6 5.293 3.354 2.646a.5.5 0 1 0-.708.708L5.293 6 2.646 8.646a.5.5 0 1 0 .708.708L6 6.707l2.646 2.647a.5.5 0 1 0 .708-.708L6.707 6Z" />
                  </svg>
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {!locked && (
        <>
          {showInput ? (
            searchDocuments ? (
              <AsyncSearchInput
                onAdd={onAdd}
                onCancel={() => setShowInput(false)}
                searchDocuments={searchDocuments}
                alreadySelectedIds={selectedDocuments.map((d) => d.id)}
              />
            ) : (
              // Fallback sin backend: Enter agrega documento mock
              <input
                type="text"
                autoFocus
                placeholder={t('master_document.crossing.addPlaceholder')}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { setShowInput(false); return; }
                  if (e.key === 'Enter') {
                    const raw = (e.target as HTMLInputElement).value.trim();
                    if (!raw) return;
                    onAdd({ id: `mock-${Date.now()}`, number: raw, classLabel: t('master_document.crossing.pending.document') });
                    setShowInput(false);
                  }
                }}
                onBlur={() => setShowInput(false)}
                className={clsx(
                  'w-full mt-2 px-2 py-1 rounded-md text-sm',
                  'border border-border-primary dark:border-dark-border-primary',
                  'bg-bg-primary dark:bg-dark-bg-primary',
                  'text-content-primary dark:text-dark-content-primary',
                  'placeholder:text-content-secondary dark:placeholder:text-dark-content-secondary',
                  'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 dark:focus:ring-dark-border-custom',
                )}
              />
            )
          ) : (
            <button
              type="button"
              onClick={() => setShowInput(true)}
              className={inlineActionButton}
            >
              ＋ {t('master_document.crossing.addSourceDocument')}
            </button>
          )}
        </>
      )}
    </div>
  );
}
